export async function rotateBase64Image(
  base64: string,
  degrees: number,
  quality = 0.8,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      const radians = (degrees * Math.PI) / 180;
      const isVertical = degrees % 180 !== 0;
      const { width, height } = img;

      canvas.width = isVertical ? height : width;
      canvas.height = isVertical ? width : height;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(img, -width / 2, -height / 2);

      // Detect original format and optimize output
      const originalFormat = getImageFormatFromBase64(base64);
      let outputFormat = 'image/jpeg'; // Default to JPEG for better compression
      let outputQuality = quality;

      // If original is PNG and has transparency, use PNG but with optimization
      if (originalFormat === 'png' && hasTransparency(base64)) {
        outputFormat = 'image/png';
        outputQuality = 0.9; // PNG doesn't use quality parameter but we can optimize elsewhere
      }
      resolve(canvas.toDataURL(outputFormat, outputQuality));
    };

    img.onerror = (e) => {
      reject(new Error('Image load failed'));
    };
  });
}

// Helper function to detect image format from base64
function getImageFormatFromBase64(base64: string): string {
  if (base64.startsWith('data:image/jpeg')) return 'jpeg';
  if (base64.startsWith('data:image/png')) return 'png';
  if (base64.startsWith('data:image/webp')) return 'webp';
  if (base64.startsWith('data:image/gif')) return 'gif';
  return 'jpeg'; // Default
}

// Helper function to check if image has transparency (simplified check)
function hasTransparency(base64: string): boolean {
  // For PNG, we assume it might have transparency
  // In a real implementation, you'd need to decode the image data
  return base64.startsWith('data:image/png');
}

// Utility function to optimize image size
export async function optimizeBase64Image(
  base64: string,
  options: {
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
    format?: 'jpeg' | 'png' | 'webp';
  } = {},
): Promise<string> {
  const { quality = 0.8, maxWidth, maxHeight, format } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      let { width, height } = img;

      // Resize if max dimensions are specified
      if (maxWidth || maxHeight) {
        const ratio = Math.min(
          maxWidth ? maxWidth / width : 1,
          maxHeight ? maxHeight / height : 1,
        );

        if (ratio < 1) {
          width *= ratio;
          height *= ratio;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image with new dimensions
      ctx.drawImage(img, 0, 0, width, height);

      // Determine output format
      let outputFormat = 'image/jpeg';
      if (
        format === 'png' ||
        (format === undefined && hasTransparency(base64))
      ) {
        outputFormat = 'image/png';
      } else if (format === 'webp') {
        outputFormat = 'image/webp';
      }

      resolve(canvas.toDataURL(outputFormat, quality));
    };

    img.onerror = () => {
      reject(new Error('Image load failed'));
    };
  });
}

export async function mirrorBase64Image(
  base64: string,
  horizontal = true,
  quality = 0.8,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      ctx.save();
      if (horizontal) {
        ctx.scale(-1, 1);
        ctx.drawImage(img, -img.width, 0);
      } else {
        ctx.scale(1, -1);
        ctx.drawImage(img, 0, -img.height);
      }
      ctx.restore();

      // Detect original format and optimize output
      const originalFormat = getImageFormatFromBase64(base64);
      let outputFormat = 'image/jpeg'; // Default to JPEG for better compression
      let outputQuality = quality;

      // If original is PNG and has transparency, use PNG but with optimization
      if (originalFormat === 'png' && hasTransparency(base64)) {
        outputFormat = 'image/png';
        outputQuality = 0.9; // PNG doesn't use quality parameter but we can optimize elsewhere
      }

      resolve(canvas.toDataURL(outputFormat, outputQuality));
    };
    img.onerror = (e) => {
      reject(new Error('Image load failed'));
    };
  });
}

export async function getWidthAndHeightFromBase64(base64: string): Promise<{
  width: number;
  height: number;
}> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = (e) => {
      reject(new Error('Image load failed'));
    };
  });
}
